// Contact page template — clean, professional contact layout with routing, SLAs, and form
"use client";

import React, { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { contactPost } from "@/tools/calls";
import Section from "@/components/public/Section";
import { DarkHeroHeader } from "@/components/public/DarkHeroHeader";
import { HomepageFAQs } from "@/components/public/homepage-FAQs";
import FeatureGrid from "@/components/public/FeatureGrid";
import { ContactSlaBlock } from "@/components/public/ContactSlaBlock";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Check,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  ListChecks,
  ShieldCheck,
} from "lucide-react";

// ─── Data interfaces ──────────────────────────────────────────────────────────

export interface ContactInfo {
  email: string;
  emailLink: string;
  phone: string;
  phoneLink: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
  };
  hours: {
    weekdays: string;
    weekdayTimes: string;
    weekends?: string;
    weekendTimes?: string;
  };
}

export interface ContactFaq {
  question: string;
  answer: string;
}

export interface InquiryType {
  value: string;
  label: string;
}

export interface UrgencyLevel {
  value: string;
  label: string;
}

export interface ContactRoute {
  icon: string;
  title: string;
  description: string;
}

export interface SlaInfo {
  standard: string;
  priority: string;
  emergency: string;
}

export interface ContactData {
  eyebrow: string;
  title: string;
  description: string;
  contactInfo: ContactInfo;
  benefitBullets?: string[];
  contactRoutes?: ContactRoute[];
  slaInfo?: SlaInfo;
  resolveChecklist?: string[];
  inquiryTypes: InquiryType[];
  urgencyLevels?: UrgencyLevel[];
  faqs: ContactFaq[];
}

// ─── ContactTemplate ──────────────────────────────────────────────────────────

interface ContactTemplateProps {
  contact: ContactData;
}

export function ContactTemplate({ contact }: ContactTemplateProps) {
  const {
    eyebrow,
    title,
    description,
    contactInfo,
    benefitBullets,
    contactRoutes,
    slaInfo,
    resolveChecklist,
    inquiryTypes,
    urgencyLevels,
    faqs,
  } = contact;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    portfolioSize: "",
    inquiryType: "",
    urgency: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await contactPost({
        email: formData.email.trim(),
        date_submitted: new Date().toISOString(),
        contacted: 0,
        submission_type: "contact",
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        company: formData.company.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        portfolio_size: formData.portfolioSize.trim() || undefined,
        inquiry_type: formData.inquiryType,
        urgency: formData.urgency || undefined,
        message: formData.message.trim(),
        page: "contact",
      });

      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Could not submit your request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmitError(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      phone: "",
      portfolioSize: "",
      inquiryType: "",
      urgency: "",
      message: "",
    });
  };

  // Map contact routes to FeatureGrid format
  const routeFeatures =
    contactRoutes?.map((route) => ({
      icon: route.icon,
      title: route.title,
      description: route.description,
    })) ?? [];

  return (
    <>
      {/* Dark hero header */}
      <DarkHeroHeader eyebrow={eyebrow} title={title} description={description}>
        {benefitBullets && benefitBullets.length > 0 && (
          <ul className="mt-6 space-y-2.5">
            {benefitBullets.map((bullet, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-white/60">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-400" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        )}
      </DarkHeroHeader>

      {/* Contact Routes — FeatureGrid */}
      {routeFeatures.length > 0 && (
        <Section spacing="relaxed" background="transparent">
          <FeatureGrid
            features={routeFeatures}
            cols={routeFeatures.length >= 4 ? 4 : 3}
            showAccent={true}
          />
        </Section>
      )}

      {/* Contact Form + Info Cards — gray background */}
      <Section spacing="relaxed" bgColor="#f8fafc">
        {/* Section header */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-gray-900 mb-2">
              Submit a request
            </h2>
            <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed">
              Fill out the form and we&apos;ll route it to the right team.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: "#a855f7" }}
                  >
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Request Submitted!
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Thanks for reaching out. We&apos;ll respond based on your
                    support plan and urgency.
                  </p>
                  <button
                    onClick={resetForm}
                    className="font-medium transition-colors"
                    style={{ color: "#a855f7" }}
                  >
                    Send another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5">
                        First Name <span style={{ color: "#a855f7" }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Last Name <span style={{ color: "#a855f7" }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  {/* Email + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email <span style={{ color: "#a855f7" }}>*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Phone <span className="text-gray-400">(Optional)</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all"
                        placeholder="+44 7700 900000"
                      />
                    </div>
                  </div>

                  {/* Company + Portfolio size */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Company <span className="text-gray-400">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label htmlFor="portfolioSize" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Portfolio Size <span className="text-gray-400">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        id="portfolioSize"
                        name="portfolioSize"
                        value={formData.portfolioSize}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all"
                        placeholder="e.g. 50 properties"
                      />
                    </div>
                  </div>

                  {/* Inquiry Type + Urgency */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Inquiry Type <span style={{ color: "#a855f7" }}>*</span>
                      </label>
                      <select
                        id="inquiryType"
                        name="inquiryType"
                        value={formData.inquiryType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all appearance-none"
                      >
                        {inquiryTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {urgencyLevels && (
                      <div>
                        <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1.5">
                          Urgency <span className="text-gray-400">(Optional)</span>
                        </label>
                        <select
                          id="urgency"
                          name="urgency"
                          value={formData.urgency}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all appearance-none"
                        >
                          {urgencyLevels.map((level) => (
                            <option key={level.value} value={level.value}>
                              {level.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Message <span style={{ color: "#a855f7" }}>*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all resize-none"
                      placeholder="How can we help you? Include any relevant details such as property IDs, PMS references, or delivery requirements."
                    />
                  </div>

                  {submitError && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {submitError}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3.5 rounded-xl font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: "#a855f7" }}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Submit Request
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info Cards */}
          <div className="lg:col-span-2 space-y-5">
            {/* Quick Contact */}
            <div
              className="rounded-2xl p-6 text-white"
              style={{ backgroundColor: "var(--homepage-primary, #0d2434)" }}
            >
              <h3 className="text-lg font-bold mb-5">Quick Contact</h3>
              <div className="space-y-4">
                <a href={contactInfo.emailLink} className="flex items-center gap-3 group">
                  <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Email</p>
                    <p className="text-sm font-medium group-hover:underline">{contactInfo.email}</p>
                  </div>
                </a>
                <a href={contactInfo.phoneLink} className="flex items-center gap-3 group">
                  <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Phone</p>
                    <p className="text-sm font-medium group-hover:underline">{contactInfo.phone}</p>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Address</p>
                    <p className="text-sm font-medium">{contactInfo.address.line1}</p>
                    <p className="text-sm font-medium">{contactInfo.address.line2}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-bold text-gray-900">Business Hours</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{contactInfo.hours.weekdays}</span>
                  <span className="font-medium text-gray-900">{contactInfo.hours.weekdayTimes}</span>
                </div>
                {contactInfo.hours.weekends && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{contactInfo.hours.weekends}</span>
                    <span className="font-medium text-gray-900">{contactInfo.hours.weekendTimes}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#a855f7" }} />
                <span className="text-xs font-medium text-gray-600">Emergency support available 24/7</span>
              </div>
            </div>

            {/* Quick Links */}
            <div className="rounded-2xl bg-white border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Links</h3>
              <div className="space-y-2">
                {[
                  { href: "/solutions", label: "Solutions Overview" },
                  { href: "/help-center", label: "Help Center" },
                  { href: "/about", label: "About Us" },
                ].map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors group"
                  >
                    <ArrowRight className="w-3 h-3 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* SLA + Resolve faster — white background, imported component */}
      {(slaInfo || resolveChecklist) && (
        <Section spacing="relaxed" background="white">
          <ContactSlaBlock slaInfo={slaInfo} resolveChecklist={resolveChecklist} />
        </Section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <>
          <div className="w-full border-t border-gray-200" />
          <Section spacing="relaxed" bgColor="#fafbfc">
            <HomepageFAQs title="Frequently Asked Questions" faqs={faqs} />
          </Section>
        </>
      )}
    </>
  );
}

export default ContactTemplate;
